/*
  Dinner Points landing page behaviour (see assets/dp-points.css). Loaded by every dp-* section,
  so it guards against running twice, and re-binds when a section is re-rendered in the editor.

  - Balance panel: Loyalty Lion fills [data-lion-points] (approved) and [data-lion-points="pending"]
    itself. We watch those spans and turn the approved figure into dollars (100 points = $1, in
    $5 rewards) and a progress bar to the next reward.
  - Calculator: order amount slider × points per dollar (5, or 10 for Dinner Club members).
  - Referral link: copy [data-lion-referral-url] to the clipboard.
  - Earn cards: open Loyalty Lion rule modals / Klaviyo sign-up pop-ups, and show a tick once the rule is completed.
  - Referral page: WhatsApp / email / text / native share buttons built from the referral link.
*/
(() => {
  if (window.dpPoints) return;
  window.dpPoints = true;

  const num = (text) => parseInt(String(text || '').replace(/[^\d]/g, ''), 10) || 0;
  const fmt = (n) => n.toLocaleString('en-AU');
  const money = (n) => '$' + n.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  /* ---------- Balance → dollars + progress ---------- */
  // Only write when the text changes, so the observer below never re-triggers itself
  const setText = (el, text) => { if (el && el.textContent !== text) el.textContent = text; };

  const renderBalance = (panel) => {
    const src = panel.querySelector('[data-lion-points]:not([data-lion-points="pending"])');
    if (!src) return;
    const points = num(src.textContent);
    const perDollar = Number(panel.dataset.dpPointsPerDollar) || 100;
    const step = Number(panel.dataset.dpRewardStep) || 500;
    const max = Number(panel.dataset.dpRewardMax) || 2500;

    const worth = panel.querySelector('[data-dp-worth]');
    if (worth) {
      const dollars = Math.floor(points / step) * (step / perDollar);
      setText(worth, dollars > 0 ? `= ${money(dollars)} in Dinner Rewards` : 'Your first $5 reward is on its way');
    }

    const bar = panel.querySelector('[data-dp-bar]');
    const text = panel.querySelector('[data-dp-progress-text]');
    if (bar && text) {
      if (points >= max) {
        bar.style.width = '100%';
        setText(text, `You can claim a ${money(max / perDollar)} Dinner Reward right now.`);
      } else {
        const next = Math.max(step, Math.ceil((points + 1) / step) * step);
        const base = next - step;
        bar.style.width = `${Math.round(((points - base) / step) * 100)}%`;
        setText(text, `${fmt(next - points)} points to your next ${money(next / perDollar)} Dinner Reward`);
      }
    }

    const pendingWrap = panel.querySelector('[data-dp-pending]');
    const pendingSrc = panel.querySelector('[data-lion-points="pending"]');
    if (pendingWrap && pendingSrc) pendingWrap.hidden = num(pendingSrc.textContent) === 0;
  };

  const initBalance = (root) => {
    root.querySelectorAll('[data-dp-balance]').forEach((panel) => {
      if (panel.dataset.dpBound) return;
      panel.dataset.dpBound = 'true';
      renderBalance(panel);
      // Watch only the spans Loyalty Lion writes into, not the whole panel (which we write into)
      if ('MutationObserver' in window) {
        const observer = new MutationObserver(() => renderBalance(panel));
        panel.querySelectorAll('[data-lion-points]').forEach((el) => observer.observe(el, { childList: true, subtree: true, characterData: true }));
      }
      // Loyalty Lion may fill the spans a little after load
      [600, 1500, 3500].forEach((t) => setTimeout(() => renderBalance(panel), t));
    });
  };

  /* ---------- Calculator ---------- */
  const initCalc = (root) => {
    root.querySelectorAll('[data-dp-calc]').forEach((calc) => {
      if (calc.dataset.dpBound) return;
      calc.dataset.dpBound = 'true';
      const range = calc.querySelector('input[type="range"]');
      const out = calc.querySelector('[data-dp-calc-amount]');
      const pointsEl = calc.querySelector('[data-dp-calc-points]');
      const worthEl = calc.querySelector('[data-dp-calc-worth]');
      const yearEl = calc.querySelector('[data-dp-calc-year]');
      const toggles = calc.querySelectorAll('[data-dp-rate]');
      const perDollarValue = Number(calc.dataset.dpPerDollar) || 100;
      const ordersPerYear = Number(calc.dataset.dpOrdersPerYear) || 12;
      let rate = Number(calc.dataset.dpRateDefault) || 5;

      const render = () => {
        const amount = Number(range.value);
        const points = amount * rate;
        if (out) out.textContent = money(amount);
        if (pointsEl) pointsEl.textContent = fmt(points);
        if (worthEl) worthEl.textContent = '$' + (points / perDollarValue).toFixed(2).replace(/\.00$/, '');
        if (yearEl) yearEl.textContent = money(Math.floor((points * ordersPerYear) / perDollarValue));
        toggles.forEach((b) => b.setAttribute('aria-pressed', String(Number(b.dataset.dpRate) === rate)));
      };
      range.addEventListener('input', render);
      toggles.forEach((b) => b.addEventListener('click', () => { rate = Number(b.dataset.dpRate); render(); }));
      render();
    });
  };

  /* ---------- Copy referral link ---------- */
  const initCopy = (root) => {
    root.querySelectorAll('[data-dp-copy]').forEach((btn) => {
      if (btn.dataset.dpBound) return;
      btn.dataset.dpBound = 'true';
      const label = btn.querySelector('span');
      const original = label ? label.textContent : '';
      btn.addEventListener('click', async () => {
        const target = document.querySelector(btn.dataset.dpCopy);
        const value = target ? target.textContent.trim() : '';
        if (!value) return;
        try {
          await navigator.clipboard.writeText(value);
          btn.classList.add('is-copied');
          if (label) label.textContent = btn.dataset.dpCopied || 'Copied!';
          setTimeout(() => { btn.classList.remove('is-copied'); if (label) label.textContent = original; }, 2000);
        } catch (e) {
          window.prompt('Copy your referral link', value);
        }
      });
    });
  };

  /* ---------- Sign in and come back here ---------- */
  // Liquid builds return_to from request.path; keep any ?view= preview parameter and add welcome=1
  const initLogin = (root) => {
    root.querySelectorAll('a[href*="/customer_authentication/login"]').forEach((a) => {
      if (a.dataset.dpBound) return;
      a.dataset.dpBound = 'true';
      const back = new URL(window.location.href);
      back.searchParams.delete('welcome');
      back.searchParams.set('welcome', '1');
      const url = new URL(a.getAttribute('href'), window.location.origin);
      url.searchParams.set('return_to', back.pathname + back.search + (a.dataset.dpReturnHash || ''));
      a.setAttribute('href', url.pathname + url.search);
    });
  };

  // Welcome card: dp-hero renders it only for a brand-new account. Show it once per customer, on the
  // first visit after sign-in (?welcome=1, or simply the first time this browser sees it), then
  // remember so it never comes back. The welcome flag is dropped from the URL either way.
  const initWelcome = () => {
    const params = new URLSearchParams(window.location.search);
    const flagged = params.get('welcome') === '1';
    const box = document.querySelector('[data-dp-welcome]');
    if (box) {
      const key = 'dp-welcome-shown-' + (box.dataset.dpWelcome || 'customer');
      let seen = false;
      try { seen = window.localStorage.getItem(key) === '1'; } catch (e) { /* storage blocked */ }
      if (flagged || !seen) {
        box.hidden = false;
        try { window.localStorage.setItem(key, '1'); } catch (e) { /* ignore */ }
        const close = box.querySelector('[data-dp-welcome-close]');
        if (close) close.addEventListener('click', () => { box.hidden = true; });
      }
    }
    if (flagged) {
      params.delete('welcome');
      const clean = window.location.pathname + (params.toString() ? '?' + params.toString() : '') + window.location.hash;
      if (window.history.replaceState) window.history.replaceState(null, '', clean);
    }
  };

  /* ---------- Loyalty Lion rule pop-ups (birthday form, Instagram / Facebook follow) ---------- */
  // dp-earn renders a hidden [data-lion-rules-list] for signed-in customers. Clicking a card clicks
  // Loyalty Lion's own rule item, which opens LL's modal and records the points. If no modal shows
  // up (LL not loaded, rule disabled) the card falls back to its link.
  const lionModalVisible = () => [...document.querySelectorAll('[class*="lion-modal"], [class*="lion-rule-action"], [class*="lion-dialog"]')]
    .some((el) => el.offsetParent !== null || getComputedStyle(el).position === 'fixed');
  // Completed state: Loyalty Lion marks a finished rule item with lion-rule-item--completed-forever
  // (one-off rules) or renders a "completed" / "wait until…" element inside it (limited rules such as
  // the birthday). Cards with data-dp-lion-status mirror that with a tick and stop opening anything.
  const lionItemDone = (item) => Boolean(item && (item.classList.contains('lion-rule-item--completed-forever')
    || item.querySelector('[class*="rule-item__completed"], [class*="rule-item__limit-reached"]')));
  const markDone = (card) => {
    if (card.dataset.dpDone) return;
    card.dataset.dpDone = 'true';
    card.classList.add('is-done');
    card.setAttribute('aria-disabled', 'true');
    const tick = card.querySelector('.dp-earn__done');
    if (tick) tick.hidden = false;
  };
  const syncLionDone = () => {
    const list = document.querySelector('[data-lion-rules-list]');
    if (!list) return;
    document.querySelectorAll('[data-dp-lion-status]').forEach((card) => {
      const item = list.querySelector(`.lion-rule-item--${card.dataset.dpLionStatus}`);
      if (lionItemDone(item)) markDone(card);
    });
  };
  let lionWatched = false;
  const watchLion = () => {
    if (lionWatched) return;
    const list = document.querySelector('[data-lion-rules-list]');
    if (!list) return;
    lionWatched = true;
    syncLionDone();
    if ('MutationObserver' in window) new MutationObserver(syncLionDone).observe(list, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    [1000, 3000, 7000].forEach((t) => setTimeout(syncLionDone, t));
    if (window.loyaltylion && typeof window.loyaltylion.on === 'function') {
      try { window.loyaltylion.on('ready', syncLionDone); } catch (e) { /* ignore */ }
    }
  };
  const initLion = (root) => {
    watchLion();
    root.querySelectorAll('[data-dp-lion-rule]').forEach((btn) => {
      if (btn.dataset.dpBound) return;
      btn.dataset.dpBound = 'true';
      btn.addEventListener('click', () => {
        if (btn.dataset.dpDone) return;
        const id = btn.dataset.dpLionRule;
        const fallback = btn.dataset.dpFallback;
        const item = id && document.querySelector(`[data-lion-rules-list] .lion-rule-item--${id}`);
        const target = item && (item.querySelector('button, a, [role="button"]') || item);
        if (target) target.click();
        if (!target && window.loyaltylion && window.loyaltylion.ui && window.loyaltylion.ui.openRuleActionModal) {
          try { window.loyaltylion.ui.openRuleActionModal({ id: Number(id) }); } catch (e) { /* ignore */ }
        }
        setTimeout(() => {
          if (!lionModalVisible() && fallback) window.open(fallback, fallback.startsWith('http') ? '_blank' : '_self', 'noopener');
        }, 1200);
      });
    });
    if (root.querySelector('[data-lion-rules-list]') && window.loyaltylion && window.loyaltylion.ui && window.loyaltylion.ui.refresh && root !== document) {
      try { window.loyaltylion.ui.refresh(); } catch (e) { /* ignore */ }
    }
  };

  /* ---------- Newsletter / SMS sign-up pop-up (Klaviyo) ---------- */
  const toE164 = (raw) => {
    let p = String(raw || '').replace(/[^\d+]/g, '');
    if (/^0[2-9]\d{8}$/.test(p)) p = '+61' + p.slice(1);        // 04xx xxx xxx → +614xx
    else if (/^61\d{9}$/.test(p)) p = '+' + p;                  // 614xx…
    else if (/^[1-9]\d{7,14}$/.test(p)) p = '+' + p;            // other international without +
    return /^\+\d{8,15}$/.test(p) ? p : '';
  };
  const klaviyoSubscribe = async (dialog, { email, phone, mode }) => {
    const listId = mode === 'sms' ? dialog.dataset.dpSmsList : dialog.dataset.dpEmailList;
    const company = dialog.dataset.dpCompany;
    if (!listId || !company) throw new Error('Sign-up is not configured yet.');
    const attributes = mode === 'sms'
      ? { phone_number: phone, subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } } }
      : { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } };
    if (mode === 'sms' && email) attributes.email = email; // identify the customer's profile, no email consent change
    const body = { data: { type: 'subscription', attributes: { custom_source: dialog.dataset.dpSource || 'Dinner Points page', profile: { data: { type: 'profile', attributes } } }, relationships: { list: { data: { type: 'list', id: listId } } } } };
    const res = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(company)}`, {
      method: 'POST', headers: { 'Content-Type': 'application/vnd.api+json', revision: '2026-10-15' }, body: JSON.stringify(body)
    });
    if (!res.ok) {
      let detail = '';
      try { detail = (await res.json()).errors?.[0]?.detail || ''; } catch (e) { /* ignore */ }
      throw new Error(detail || 'Something went wrong, please try again.');
    }
  };
  // Shopify's own newsletter form: flips the customer's email consent, which Loyalty Lion's newsletter rule listens to
  const shopifyNewsletter = (email) => {
    const fd = new FormData();
    fd.append('form_type', 'customer'); fd.append('utf8', '✓'); fd.append('contact[tags]', 'newsletter'); fd.append('contact[email]', email);
    return fetch('/contact', { method: 'POST', body: fd, credentials: 'same-origin' }).catch(() => {});
  };
  const initSignup = (root) => {
    const dialog = document.querySelector('[data-dp-signup]');
    if (!dialog) return;
    const q = (sel) => dialog.querySelector(sel);
    const setMode = (mode) => {
      dialog.dataset.dpMode = mode;
      dialog.querySelectorAll('[data-email][data-sms]').forEach((el) => { el.textContent = el.dataset[mode] || ''; });
      q('[data-dp-signup-phone]').hidden = mode !== 'sms';
      q('[data-dp-signup-email]').hidden = mode === 'sms';
      q('input[name="phone"]').required = mode === 'sms';
      q('input[name="email"]').required = mode !== 'sms';
      q('[data-dp-signup-points]').textContent = mode === 'sms' ? '250' : '100';
      q('[data-dp-signup-submit-label]').textContent = mode === 'sms' ? 'Text me' : 'Sign me up';
      q('[data-dp-signup-step="form"]').hidden = false;
      q('[data-dp-signup-step="done"]').hidden = true;
      q('[data-dp-signup-error]').hidden = true;
    };
    root.querySelectorAll('[data-dp-signup-open]').forEach((btn) => {
      if (btn.dataset.dpBound) return;
      btn.dataset.dpBound = 'true';
      btn.addEventListener('click', () => {
        if (btn.dataset.dpDone) return;
        dialog.dataset.dpOpener = btn.dataset.dpLionStatus || '';
        setMode(btn.dataset.dpSignupOpen === 'sms' ? 'sms' : 'email');
        if (typeof dialog.showModal === 'function') dialog.showModal(); else dialog.setAttribute('open', '');
        const first = dialog.dataset.dpMode === 'sms' ? q('input[name="phone"]') : q('input[name="email"]');
        setTimeout(() => first && first.focus(), 50);
      });
    });
    if (dialog.dataset.dpBound) return;
    dialog.dataset.dpBound = 'true';
    dialog.querySelectorAll('[data-dp-signup-close]').forEach((b) => b.addEventListener('click', () => dialog.close()));
    dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });
    const form = q('form');
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const mode = dialog.dataset.dpMode || 'email';
      const err = q('[data-dp-signup-error]');
      const email = (mode === 'sms' ? (q('input[name="customer_email"]').value || q('input[name="email"]').value) : q('input[name="email"]').value).trim();
      const phone = mode === 'sms' ? toE164(q('input[name="phone"]').value) : '';
      const consent = q('input[name="consent"]').checked;
      const fail = (msg) => { err.textContent = msg; err.hidden = false; };
      if (mode !== 'sms' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return fail('Please enter a valid email address.');
      if (mode === 'sms' && !phone) return fail('Please enter an Australian mobile number, e.g. 0412 345 678.');
      if (!consent) return fail('Please tick the box so we have your permission.');
      err.hidden = true;
      const submit = q('.dp-modal__submit');
      submit.disabled = true;
      try {
        await klaviyoSubscribe(dialog, { email, phone, mode });
        if (mode === 'email') await shopifyNewsletter(email);
        q('[data-dp-signup-step="form"]').hidden = true;
        q('[data-dp-signup-step="done"]').hidden = false;
        if (dialog.dataset.dpOpener) document.querySelectorAll(`[data-dp-lion-status="${dialog.dataset.dpOpener}"]`).forEach(markDone);
        if (window.loyaltylion && window.loyaltylion.ui && window.loyaltylion.ui.refresh) setTimeout(() => { try { window.loyaltylion.ui.refresh(); } catch (e) { /* ignore */ } }, 2000);
      } catch (ex) {
        fail(ex.message || 'Something went wrong, please try again.');
      } finally {
        submit.disabled = false;
      }
    });
  };

  /* ---------- Referral share buttons (referral page hero) ---------- */
  // Loyalty Lion fills [data-lion-referral-url] a moment after load. Once it has, the WhatsApp /
  // email / text buttons get real hrefs built from the share message, and the native share sheet
  // button appears where the browser supports it.
  const initShare = (root) => {
    root.querySelectorAll('[data-dp-share-panel]').forEach((panel) => {
      if (panel.dataset.dpBound) return;
      panel.dataset.dpBound = 'true';
      const src = panel.querySelector('[data-lion-referral-url]');
      if (!src) return;
      const message = panel.dataset.dpShareMessage || '';
      const subject = panel.dataset.dpShareSubject || 'Dinner is on me';
      const apply = () => {
        const url = src.textContent.trim();
        if (!/^https?:\/\//.test(url)) return;
        const text = message ? `${message} ${url}` : url;
        panel.querySelectorAll('[data-dp-share]').forEach((a) => {
          switch (a.dataset.dpShare) {
            case 'whatsapp': a.href = 'https://wa.me/?text=' + encodeURIComponent(text); break;
            case 'email': a.href = 'mailto:?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(text); break;
            case 'sms': a.href = 'sms:?&body=' + encodeURIComponent(text); break;
            case 'native':
              if (navigator.share) {
                a.hidden = false;
                a.onclick = (e) => { e.preventDefault(); navigator.share({ title: subject, text: message, url }).catch(() => {}); };
              }
              break;
            default: break;
          }
        });
        panel.classList.add('is-ready');
      };
      apply();
      if ('MutationObserver' in window) new MutationObserver(apply).observe(src, { childList: true, subtree: true, characterData: true });
      [800, 2000, 4500].forEach((t) => setTimeout(apply, t));
    });
  };

  // Dinner Club page: "does it pay for itself?" (sections/dp-perks.liquid). Monthly order slider ->
  // 10% off (orders up to the cap), delivery saved between the member and standard thresholds,
  // extra points value, minus the membership price.
  const initClubCalc = (root) => {
    root.querySelectorAll('[data-dp-club-calc]').forEach((calc) => {
      if (calc.dataset.dpBound) return;
      calc.dataset.dpBound = 'true';
      const range = calc.querySelector('input[type="range"]');
      if (!range) return;
      const d = calc.dataset;
      const price = Number(d.dpPrice) || 9.99;
      const discount = (Number(d.dpDiscount) || 10) / 100;
      const cap = Number(d.dpDiscountCap) || 500;
      const delivery = Number(d.dpDelivery) || 17;
      const memberFree = Number(d.dpMemberFree) || 100;
      const standardFree = Number(d.dpStandardFree) || 150;
      const extraPoints = Number(d.dpExtraPoints) || 5;
      const perDollar = Number(d.dpPerDollar) || 100;
      const q = (sel) => calc.querySelector(sel);
      const dollars = (n) => '$' + n.toFixed(2).replace(/\.00$/, '');
      const render = () => {
        const amount = Number(range.value);
        const off = Math.min(amount, cap) * discount;
        const ship = amount >= memberFree && amount < standardFree ? delivery : 0;
        const points = amount * extraPoints;
        const pointsWorth = points / perDollar;
        const total = off + ship + pointsWorth;
        const net = total - price;
        setText(q('[data-dp-cc-amount]'), money(amount));
        setText(q('[data-dp-cc-discount]'), dollars(off));
        setText(q('[data-dp-cc-delivery]'), dollars(ship));
        const shipRow = q('[data-dp-cc-delivery-row]');
        if (shipRow) shipRow.classList.toggle('is-zero', ship === 0);
        setText(q('[data-dp-cc-points]'), fmt(points));
        setText(q('[data-dp-cc-points-worth]'), dollars(pointsWorth));
        setText(q('[data-dp-cc-total]'), dollars(total));
        setText(q('[data-dp-cc-price]'), '\u2212' + dollars(price));
        const verdict = q('[data-dp-cc-verdict]');
        if (verdict) {
          const ahead = net >= 0;
          verdict.textContent = (ahead ? verdict.dataset.dpCcAhead : verdict.dataset.dpCcBehind).replace('[amount]', dollars(Math.abs(net)));
          verdict.classList.toggle('is-behind', !ahead);
        }
      };
      range.addEventListener('input', render);
      render();
    });
  };

  const init = (root = document) => { initBalance(root); initCalc(root); initCopy(root); initLogin(root); initLion(root); initSignup(root); initShare(root); initClubCalc(root); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initWelcome);
  else initWelcome();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
